/**
 * Setting material slots' base colors, roughness, metallic and own opacity,
 * and objects' opacity, as one undo step, without clobbering a property
 * binding.
 *
 * Each write goes where the editor's own picker or slider sends it
 * (`setMaterialColorForState`, `setMaterialFactorForState`,
 * `setOpacityForState`, `setTextOpacityForState`): a field bound to a
 * property updates the property, and while a state shows on the object or
 * its group, the value becomes that state's, binding the field first if it
 * isn't bound.
 */

import type { SceneObject } from '../../core/SceneObject';
import { Renderer } from '../../behaviors/renderer';
import { Properties } from '../../behaviors/properties';
import type { PropertiesData } from '../../scenedata/behaviors/propertiesdata';
import { Text } from '../../behaviors/text';
import { WHOLE_REF_RE, boundPropertyName, wholeRefName } from '../../behaviors/tracebehavior';
import type { UndoCommand } from '../undo/UndoCommand';
import { AddBehaviorCommand } from '../undo/AddBehaviorCommand';
import { BehaviorCommitCommand } from '../undo/BehaviorCommitCommand';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import { MaterialPreviews } from '../MaterialPreviews';
import { bindRendererProperty, bindingDeclaration, setMaterialColorForState, setMaterialFactorForState, setOpacityForState } from '../renderer-bindings';
import { colorAlpha, setTextOpacityForState } from '../text-bindings';
import { editTargetState, nearestStateAncestor, seedMemberStatesFromGroup } from '../states/statesActions';
import * as statesModel from '../states/statesModel';
import { isSharedIntent, pageScopedContentRoots } from '../pages/pageContext';
import { pageStateRefusal, pageStateRefusalIfAsked, routeOf } from './editRoute';
import { keyframeRefusal, prepareBatch, refuse } from './checks';
import { bundle, rollBack } from './steps';
import type { AuthoringPorts, BatchResult, Refusal } from './types';

/** One material slot's new base color. */
export interface ColorEntry {
    object: SceneObject;
    /** `#rrggbb` or `#rrggbbaa`. */
    color: string;
    /**
     * The slot: a mesh index on an object that has material slots of its own.
     * On one that has none, such as a group, the slot's position among its
     * subtree's (see {@link setMaterialColors}). Default 0.
     */
    slot?: number;
}

/** One material slot's new roughness, metallic or own opacity (its `materials[]` entry's `opacity`). */
export interface FactorEntry {
    object: SceneObject;
    factor: 'roughness' | 'metallic' | 'opacity';
    /** From 0 to 1. */
    value: number;
    /** The slot, as for {@link ColorEntry}. Default 0. */
    slot?: number;
}

/** One object's new whole-object opacity (its Renderer's `opacity`). */
export interface OpacityEntry {
    object: SceneObject;
    /** From 0 (transparent) to 1 (opaque). */
    opacity: number;
}

/** Where an entry's color is written: the object that has the slot, and the slot's mesh index on it. */
interface Write {
    object: SceneObject;
    slot: number;
    color: string;
    /** Why the entry names no slot there is. */
    refusal?: Refusal;
}

interface FactorWrite {
    object: SceneObject;
    slot: number;
    factor: FactorEntry['factor'];
    value: number;
    refusal?: Refusal;
}

const HEX = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;

/**
 * Set each entry's slot color, in the order given, as one undo step. Outside
 * a showing state, an entry whose slot already shows the color, as a literal
 * or through its property, records nothing.
 *
 * An object with no material slots of its own passes slot `n` to the `n`th
 * slot of its subtree: its children in order, each one's own slots, or else
 * its children's. With no slots there either, the object takes slot 0 itself.
 * An object without a `Renderer` gets one, in the same step. `applied`, and a
 * refusal's `object`, are the objects written to (the entry's own object when
 * its slot isn't there); match a refusal to its entry by `entry`.
 *
 * Copies a placed reference first, and refuses, as `setTransforms` does: a
 * showing state takes the color as its value, and a selected keyframe is
 * refused (`STATE_ROUTED`). So is a slot the object or its subtree doesn't
 * have, or doesn't have yet, and a slot on a Text object, which colors
 * itself (`INVALID_SLOT`).
 *
 * With `ports.pageEditing`, shared content of a Pages container takes the
 * color into the showing page's state while page editing is on, and is
 * refused (`STATE_ROUTED`) into the state that page assigns while it is off,
 * because that state may be other pages' too. Without the port the color
 * goes into the showing state, as it did before pages had one.
 *
 * A deck's slots are those of what it shows
 * (`pageScopedContentRoots`): its page, or its shared content while the user
 * authors that; a deck showing nothing or several pages has none.
 *
 * A color that is not hex, or a slot that is not a whole number, throws
 * before anything changes, in any mode, as `setTransforms`' vector check
 * does. A write the property refuses, such as a slot bound to a number,
 * throws after the batch is taken back.
 */
export async function setMaterialColors(
    entries: readonly ColorEntry[],
    ports: AuthoringPorts = {},
): Promise<BatchResult<SceneObject> & { orphaned: string[] }> {
    if (entries.length === 0) return { applied: [], refused: [], command: null, orphaned: [] };
    for (const { object, color, slot } of entries) {
        if (typeof color !== 'string' || !HEX.test(color)) {
            throw new TypeError(`[setMaterialColors] the color for "${object.name}" must be #rrggbb or #rrggbbaa, got ${JSON.stringify(color)}`);
        }
        if (slot !== undefined && !(Number.isInteger(slot) && slot >= 0)) {
            throw new TypeError(`[setMaterialColors] the slot for "${object.name}" must be a whole number, got ${String(slot)}`);
        }
    }
    const writes = entries.map(({ object, color, slot = 0 }) => ({ ...slotOwner(object, slot), color }));
    const routed = (object: SceneObject): Refusal | null =>
        keyframeRefusal(object, 'a color') ?? pageStateRefusalIfAsked(object, ports, editTargetState, 'a color');
    const { ready, refused, steps, orphaned } = await prepareBatch(writes, ports, {
        check: ({ object, refusal }) => refusal ?? textRefusal(object, 'color') ?? routed(object),
        late: ({ object }) => routed(object),
        changes: write => changes(write, ports),
    });

    const applied: SceneObject[] = [];
    const colored = new Set<SceneObject>();
    try {
        for (const { entry } of ready) {
            const { object, slot, color } = entry;
            applied.push(object);
            if (!changes(entry, ports)) continue;
            const added = await ensureRenderer(object);
            if (added) steps.push(added);
            const result = await setMaterialColorForState(object, color, { slot, record: false, pageEditing: ports.pageEditing });
            if (result.errors.length > 0 || !result.command) {
                const why = result.errors.map(error => error.message).join('; ') || 'nothing was written';
                throw new Error(`[setMaterialColors] slot ${slot} of "${object.name}" could not take ${color}: ${why}`);
            }
            steps.push(result.command);
            colored.add(object);
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const named = colored.size > 0 ? [...colored] : applied;
    const label = named.length === 1 ? `Color ${named[0].name}` : `Color ${named.length} objects`;
    return { applied, refused, command: steps.length > 0 ? bundle(label, steps) : null, orphaned };
}

/**
 * Set each entry's slot roughness, metallic or own opacity, in the order given,
 * as one undo step. Slots are found, and a placed reference is copied first, as
 * {@link setMaterialColors} does. Outside a showing state or page, an entry whose
 * slot already shows the value, as a literal or through its property, records
 * nothing.
 *
 * The value goes where it is seen: a showing state takes it, binding the factor
 * to a property first, and so does the showing page for shared content of a
 * Pages container while `ports.pageEditing` says page editing is on, with a
 * state two pages share forked for that page. With page editing off, a state
 * the showing page assigns is refused (`STATE_ROUTED`), as is a selected
 * keyframe. So is a slot the object or its subtree doesn't have, and a slot on
 * a Text object (`INVALID_SLOT`).
 *
 * A factor that is not `roughness`, `metallic` or `opacity`, a value outside 0 to 1, or a
 * slot that is not a whole number throws before anything changes, in any mode. A
 * write the property refuses throws after the batch is taken back.
 */
export async function setMaterialFactors(
    entries: readonly FactorEntry[],
    ports: AuthoringPorts = {},
): Promise<BatchResult<SceneObject> & { orphaned: string[] }> {
    if (entries.length === 0) return { applied: [], refused: [], command: null, orphaned: [] };
    for (const { object, factor, value, slot } of entries) {
        if (factor !== 'roughness' && factor !== 'metallic' && factor !== 'opacity') {
            throw new TypeError(`[setMaterialFactors] the factor for "${object.name}" must be "roughness", "metallic" or "opacity", got ${JSON.stringify(factor)}`);
        }
        if (typeof value !== 'number' || !(value >= 0 && value <= 1)) {
            throw new TypeError(`[setMaterialFactors] the ${factor} for "${object.name}" must be a number from 0 to 1, got ${String(value)}`);
        }
        if (slot !== undefined && !(Number.isInteger(slot) && slot >= 0)) {
            throw new TypeError(`[setMaterialFactors] the slot for "${object.name}" must be a whole number, got ${String(slot)}`);
        }
    }
    const writes = entries.map(({ object, factor, value, slot = 0 }) => ({ ...slotOwner(object, slot), factor, value }));
    const routed = ({ object, factor }: FactorWrite): Refusal | null => {
        const edit = factor === 'opacity' ? 'a slot opacity' : `a ${factor}`;
        return keyframeRefusal(object, edit) ?? pageStateRefusal(object, ports, editTargetState, edit);
    };
    const { ready, refused, steps, orphaned } = await prepareBatch(writes, ports, {
        check: write => write.refusal ?? textRefusal(write.object, write.factor) ?? routed(write),
        late: routed,
        changes: write => factorChanges(write, ports),
    });

    const applied: SceneObject[] = [];
    const written = new Set<SceneObject>();
    try {
        for (const { entry } of ready) {
            const { object, slot, factor, value } = entry;
            applied.push(object);
            if (!factorChanges(entry, ports)) continue;
            const added = await ensureRenderer(object);
            if (added) steps.push(added);
            const result = await setMaterialFactorForState(object, factor, value, { slot, record: false, pageEditing: ports.pageEditing });
            if (result.errors.length > 0 || !result.command) {
                const why = result.errors.map(error => error.message).join('; ') || 'nothing was written';
                throw new Error(`[setMaterialFactors] slot ${slot} of "${object.name}" could not take ${factor} ${value}: ${why}`);
            }
            steps.push(result.command);
            written.add(object);
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const named = written.size > 0 ? [...written] : applied;
    const what = entries.every(entry => entry.factor === 'opacity') ? 'Opacity' : 'Finish';
    const label = named.length === 1 ? `${what} of ${named[0].name}` : `${what} of ${named.length} objects`;
    return { applied, refused, command: steps.length > 0 ? bundle(label, steps) : null, orphaned };
}

/**
 * Set each entry's whole-object opacity, in the order given, as one undo step.
 * Outside a showing state, an entry whose object already shows the opacity,
 * as a literal or through its property, records nothing; while one shows, an
 * entry is always written, since the state may hold another value. An object
 * without a `Renderer` gets one, in the same step.
 *
 * A Text object's opacity is its Text's, written by `setTextOpacityForState`:
 * every other state and page keeps what it shows, Default included, and with
 * `pageEditing` on, shared content of a Pages container takes it into the
 * showing page's state, refused into the state that page assigns while page
 * editing is off (`STATE_ROUTED`). The text shows the value times its
 * colour's alpha: a literal `#rrggbbaa` colour's alpha moves into the opacity
 * while the opacity is unbound, but a bound colour's alpha, or one written
 * after the opacity is bound, still multiplies it.
 *
 * Copies a placed reference first, and refuses, as {@link setMaterialColors}
 * does: a showing state takes the opacity as its value, binding the opacity
 * to a property first (`opacity`, or a Text's `textOpacity`), and a selected
 * keyframe is refused (`STATE_ROUTED`). Any other object's opacity is routed
 * to a page as a Text's is, but is refused into the state a page assigns only
 * when `ports.pageEditing` is passed and says page editing is off; a host that
 * passes none writes it into the showing state, as it did before pages had one.
 *
 * Written in Default on an object with states, it shows in Default only:
 * first each state that has no opacity of its own is pinned to what it shows
 * (`keepOpacityPerState`), in the same step.
 *
 * An opacity that is not a number from 0 to 1 throws before anything changes,
 * in any mode. A write the property refuses throws after the batch is taken
 * back.
 */
export async function setOpacity(
    entries: readonly OpacityEntry[],
    ports: AuthoringPorts = {},
): Promise<BatchResult<SceneObject> & { orphaned: string[] }> {
    if (entries.length === 0) return { applied: [], refused: [], command: null, orphaned: [] };
    for (const { object, opacity } of entries) {
        if (typeof opacity !== 'number' || !(opacity >= 0 && opacity <= 1)) {
            throw new TypeError(`[setOpacity] the opacity for "${object.name}" must be a number from 0 to 1, got ${String(opacity)}`);
        }
    }
    // A Text writes its own opacity, routed like a slot factor; anything else writes its
    // Renderer's the same way, kept per state first when written in Default, and is
    // refused when an unrelated "opacity" property would be overwritten. A Text is refused
    // into a page's state with no port; the others only once the host passes one.
    const late = ({ object }: OpacityEntry): Refusal | null => keyframeRefusal(object, 'an opacity')
        ?? (object.getBehavior(Text)
            ? pageStateRefusal(object, ports, editTargetState, 'a text opacity')
            : pageStateRefusalIfAsked(object, ports, editTargetState, 'an opacity'));
    const changes = (entry: OpacityEntry): boolean => opacityChanges(entry, ports);
    const check = (entry: OpacityEntry): Refusal | null => late(entry)
        ?? (!entry.object.getBehavior(Text) && changes(entry) ? opacityNameRefusal(entry.object, ports) : null);
    const { ready, refused, steps, orphaned } = await prepareBatch(entries, ports, { check, late, changes });

    const applied: SceneObject[] = [];
    const changed = new Set<SceneObject>();
    try {
        for (const { entry } of ready) {
            const { object, opacity } = entry;
            applied.push(object);
            if (!changes(entry)) continue;
            let result: { errors: { message: string }[]; command: UndoCommand | null };
            if (object.getBehavior(Text)) {
                result = await setTextOpacityForState(object, opacity, { record: false, pageEditing: ports.pageEditing });
            } else {
                const added = await ensureRenderer(object);
                if (added) steps.push(added);
                if (routeOf(object, ports, editTargetState).to === 'base') steps.push(...await keepOpacityPerState(object));
                result = await setOpacityForState(object, opacity, { record: false, pageEditing: ports.pageEditing });
            }
            if (result.errors.length > 0 || !result.command) {
                const why = result.errors.map(error => error.message).join('; ') || 'nothing was written';
                throw new Error(`[setOpacity] "${object.name}" could not take opacity ${opacity}: ${why}`);
            }
            steps.push(result.command);
            changed.add(object);
        }
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
    const named = changed.size > 0 ? [...changed] : applied;
    const label = named.length === 1 ? `Opacity of ${named[0].name}` : `Opacity of ${named.length} objects`;
    return { applied, refused, command: steps.length > 0 ? bundle(label, steps) : null, orphaned };
}

/**
 * Whether writing `opacity` would change what the object shows: always while a
 * state or page shows, which may hold another. A Text shows its opacity times
 * its colour's alpha.
 */
function opacityChanges({ object, opacity }: OpacityEntry, ports: AuthoringPorts): boolean {
    const text = object.getBehavior(Text)?.getData();
    if (routeOf(object, ports, editTargetState).to !== 'base') return true;
    const authored: unknown = text ? text.opacity : object.getBehavior(Renderer)?.getData().opacity;
    const bound = typeof authored === 'string' ? authored.match(WHOLE_REF_RE) : null;
    const shown = bound ? object.resolveProperty(wholeRefName(bound)) : authored ?? 1;
    return typeof shown !== 'number' || shown * (text ? colorAlpha(text.color) : 1) !== opacity;
}

/**
 * The object and mesh index slot `slot` of `object` is written to, or why
 * there is no such slot. Children whose names start with `__` are editor
 * helpers, never slot owners; hidden children count, so a slot number means
 * the same mesh in every state. A deck's slots are those of the one object
 * it shows (`pageScopedContentRoots`); showing none or several, it has none.
 */
function slotOwner(object: SceneObject, slot: number): { object: SceneObject; slot: number; refusal?: Refusal } {
    if (object.transform.isDisposed()) return { object, slot };
    // A deck's slots are those of what it shows, so a write never reaches a page that is not on screen.
    const scope = pageScopedContentRoots(object);
    if (scope !== null && scope.length !== 1) {
        const showing = scope.length === 0 ? 'nothing' : isSharedIntent(object) ? `${scope.length} shared objects` : 'several pages';
        return { object, slot, refusal: refuse('INVALID_SLOT', `"${object.name}" is a deck showing ${showing}, so it has no slot ${slot}; set it on an object on its page`, object) };
    }
    if (scope !== null) return slotOwner(scope[0], slot);
    const own = ownSlots(object);
    const found = own.map(index => ({ object, slot: index }));
    const walk = (node: SceneObject): void => {
        for (const child of node.getChildren()) {
            if (found.length > slot) return;
            if (child.name.startsWith('__')) continue;
            const slots = ownSlots(child);
            if (slots.length > 0) found.push(...slots.map(index => ({ object: child, slot: index })));
            else walk(child);
        }
    };
    if (own.length === 0) walk(object);
    if (found.length === 0 && slot === 0) return { object, slot };
    if (own.length > 0 ? own.includes(slot) : found[slot]) return own.length > 0 ? { object, slot } : found[slot];
    const what = own.length > 0 ? `has slots ${own.join(', ')}`
        : found.length > 0 ? `has ${found.length} slot${found.length === 1 ? '' : 's'} in its subtree`
        : 'has no material slots yet';
    return { object, slot, refusal: refuse('INVALID_SLOT', `"${object.name}" ${what}, so it has no slot ${slot}`, object) };
}

/** Why a slot on `object` takes no `property`: a Text has no material slots; it draws its own color and opacity. */
function textRefusal(object: SceneObject, property: 'color' | FactorEntry['factor']): Refusal | null {
    if (!object.getBehavior(Text)) return null;
    const why = property === 'color' || property === 'opacity'
        ? `whose ${property} is its Text's own ${property}, not a material slot's`
        : `which has no material slot to take a ${property}`;
    return refuse('INVALID_SLOT', `"${object.name}" is text, ${why}`, object);
}

/** The mesh indices of the material slots `object` has itself. */
function ownSlots(object: SceneObject): number[] {
    return MaterialPreviews.detectMaterialSlots(object).map((found, index) => found.materialIndex ?? index);
}

/** Whether writing the factor would change what the slot shows: always while a state or page shows, which may hold another. */
function factorChanges({ object, slot, factor, value }: FactorWrite, ports: AuthoringPorts): boolean {
    if (routeOf(object, ports, editTargetState).to !== 'base') return true;
    const entry = object.getBehavior(Renderer)?.getData().materials?.find(material => (material.targetMaterialIndex ?? 0) === slot);
    const authored: unknown = factor === 'opacity' ? entry?.opacity : factor === 'roughness' ? entry?.roughnessFactor : entry?.metallicFactor;
    const bound = typeof authored === 'string' ? authored.match(WHOLE_REF_RE) : null;
    const shown = bound ? object.resolveProperty(wholeRefName(bound)) : authored ?? (factor === 'opacity' ? 1 : undefined);
    return shown !== value;
}

/** Whether writing `color` would change what the slot shows: always while a state or page shows, which may hold another. */
function changes({ object, slot, color }: Write, ports: AuthoringPorts): boolean {
    if (routeOf(object, ports, editTargetState).to !== 'base') return true;
    const authored = object.getBehavior(Renderer)?.getData().materials
        ?.find(material => (material.targetMaterialIndex ?? 0) === slot)?.baseColorFactor;
    const bound = typeof authored === 'string' ? authored.match(WHOLE_REF_RE) : null;
    const shown = bound ? object.resolveProperty(wholeRefName(bound)) : authored;
    return typeof shown !== 'string' || opaque(shown) !== opaque(color);
}

function opaque(hex: string): string {
    return (hex.length === 7 ? `${hex}ff` : hex).toLowerCase();
}

/** Add a `Renderer` to `object` when it has none; the step that did, else null. */
async function ensureRenderer(object: SceneObject): Promise<UndoCommand | null> {
    if (object.getBehavior(Renderer)) return null;
    const renderer = await object.addBehavior(Renderer);
    dispatchBehaviorChanged({ objectPath: object.getPath(), behaviorType: 'Renderer', action: 'added' });
    return new AddBehaviorCommand(object, 'Renderer', renderer.getData());
}

/**
 * Why a write in Default can't give the object's opacity its own value per
 * state: its literal opacity would move onto an `opacity` property the object
 * already declares for something else, whose values the move would overwrite.
 */
function opacityNameRefusal(object: SceneObject, ports: AuthoringPorts): Refusal | null {
    if (routeOf(object, ports, editTargetState).to !== 'base' || !hasStatesAround(object)) return null;
    if (boundPropertyName(object.getBehavior(Renderer)?.getData().opacity) !== null) return null;
    if (!object.getBehavior(Properties)?.getProperty('opacity')) return null;
    return refuse('BINDING_OWNED', `"${object.name}" declares an "opacity" property its Renderer does not use, so its opacity can't get its own value per state; bind the Renderer's opacity to it or rename the property`, object);
}

function hasStatesAround(object: SceneObject): boolean {
    const own = object.getBehavior(Properties);
    return (own !== null && own.getStateNames().length > 0) || nearestStateAncestor(object) !== null;
}

/**
 * Before a write in Default on an object with states, give its whole-object
 * opacity its own value in every state without changing what any of them
 * shows, so the write lands in Default only. A bound opacity gets the states
 * that carry no value pinned to Default's; a literal one moves onto the
 * object's own `opacity` property, every state holding the value it showed.
 * The steps it applied, none at a state or with no states around. Throws,
 * having taken its steps back, when a commit is refused.
 */
async function keepOpacityPerState(object: SceneObject): Promise<UndoCommand[]> {
    if (editTargetState(object) !== undefined) return [];
    if (!hasStatesAround(object)) return [];
    const own = object.getBehavior(Properties);
    const steps: UndoCommand[] = [];
    try {
        let props = own;
        if (!props) {
            const empty = { properties: [], states: {} } as unknown as PropertiesData;
            props = await object.addBehavior(Properties, empty);
            props.primeSavedTransform();
            steps.push(new AddBehaviorCommand(object, 'Properties', empty));
        }
        const seeded = await seedMemberStatesFromGroup(object, props, { record: false });
        if (seeded) steps.push(seeded);
        const states = props.getStateNames();
        if (states.length === 0) {
            await rollBack(steps, null);
            return [];
        }
        const commit = async (patch: Partial<PropertiesData>): Promise<void> => {
            const before = props.getData();
            const errors = await props.commit(patch);
            if (errors.length > 0) throw new Error(`[setOpacity] "${object.name}" could not keep its opacity per state: ${errors.map(e => e.message).join('; ')}`);
            dispatchBehaviorChanged({ objectPath: object.getPath(), behaviorType: 'Properties', action: 'updated' });
            steps.push(new BehaviorCommitCommand(object, 'Properties', before, props.getData(), null));
        };
        const authored: unknown = object.getBehavior(Renderer)?.getData().opacity;
        const bound = boundPropertyName(authored);
        const data = props.getData();
        const numeric = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value) ? value : undefined;
        if (bound !== null) {
            let declared: unknown;
            for (let node: SceneObject | null = object; node && declared === undefined; node = node.getParent()) declared = node.getBehavior(Properties)?.getProperty(bound)?.value;
            const base = numeric(declared) ?? 1;
            let next = data;
            for (const state of states) {
                if (!Object.hasOwn(data.states?.[state]?.values ?? {}, bound)) next = statesModel.setStateValue(next, state, bound, base);
            }
            if (next !== data) await commit({ states: next.states });
            return steps;
        }
        const base = numeric(authored) ?? 1;
        let next = statesModel.setDeclarationValue(statesModel.ensureDeclaration(data, bindingDeclaration('opacity', 'opacity', base)), 'opacity', base);
        for (const state of states) next = statesModel.setStateValue(next, state, 'opacity', base);
        await commit({ properties: next.properties, states: next.states });
        const bind = await bindRendererProperty(object, 'opacity', { binding: 'opacity' }, { record: false });
        if (bind.errors.length > 0 || !bind.command) throw new Error(`[setOpacity] "${object.name}" could not bind its opacity: ${bind.errors.map(e => e.message).join('; ')}`);
        steps.push(bind.command);
        return steps;
    } catch (error) {
        await rollBack(steps, error);
        throw error;
    }
}
